// Payroll's first run (gap #12): the next pay period's dates, commission rates as people say them
// ("8%") and as the server takes them ("0.08"), and commission lines summed per person.

import { addDays, dayText } from './model.ts';
import type { PayPeriodSummary } from './types.ts';

const weekday = (date: string) => new Date(`${date}T12:00:00Z`).getUTCDay();
const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86_400_000);

/**
 * The period after the newest one, the same length and with its pay date as far after its end; or,
 * for a company's first, the two weeks that end on the last day of this payroll week (the day before
 * payroll.weekStartsOn: Saturday for a Sunday week), paid five days later.
 */
export function nextPeriod(
  newest: Pick<PayPeriodSummary, 'startDate' | 'endDate' | 'payDate'> | null,
  today: string,
  weekStartsOn = 0,
): { startDate: string; endDate: string; payDate: string } {
  if (newest) {
    const length = daysBetween(newest.startDate, newest.endDate) + 1;
    const startDate = addDays(newest.endDate, 1);
    const endDate = addDays(startDate, length - 1);
    return {
      startDate,
      endDate,
      payDate: addDays(endDate, Math.max(0, daysBetween(newest.endDate, newest.payDate))),
    };
  }
  const lastDay = (weekStartsOn + 6) % 7;
  const endDate = addDays(today, (lastDay - weekday(today) + 7) % 7);
  return { startDate: addDays(endDate, -13), endDate, payDate: addDays(endDate, 5) };
}

/** "8" or "8.5" (per cent) as the server's rate ("0.08", "0.085"); null when it isn't 0 to under 100. */
export function percentToRate(text: string): string | null {
  const t = text.trim().replace(/%$/, '').trim();
  if (!t) return '0';
  if (!/^\d{1,2}(\.\d{1,4})?$/.test(t)) return null;
  const rate = (Number(t) / 100).toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  return rate === '0' ? '0' : rate;
}

/** What each commission rule is called, as the plan's form says it. */
export const COMMISSION_RULES = [
  ['soloRate', 'Doing a job alone'],
  ['withHelperRate', 'Leading, with a helper'],
  ['helperRate', 'As a helper'],
  ['soldOnlyRate', 'Selling work someone else does'],
  ['completedOnlyRate', 'Doing work someone else sold'],
] as const;

export interface CommissionLine {
  staffId: string;
  amountCents?: number;
}

/** Commission lines summed per person, biggest first (amounts only for those who see pay). */
export function commissionsByPerson(
  lines: readonly CommissionLine[],
  names: ReadonlyMap<string, string>,
): { staffId: string; name: string; count: number; amountCents: number | null }[] {
  const by = new Map<string, { count: number; amountCents: number | null }>();
  for (const l of lines) {
    const row = by.get(l.staffId) ?? { count: 0, amountCents: 0 };
    row.count += 1;
    row.amountCents =
      l.amountCents === undefined || row.amountCents === null ? null : row.amountCents + l.amountCents;
    by.set(l.staffId, row);
  }
  return [...by.entries()]
    .map(([staffId, r]) => ({ staffId, name: names.get(staffId) ?? 'Someone', ...r }))
    .sort((a, b) => (b.amountCents ?? 0) - (a.amountCents ?? 0) || a.name.localeCompare(b.name));
}

/** A pay period's recorded funding (payPeriods.get), once the provider has taken its run. */
export interface PayrollFunding {
  fundedOn: string;
  amountCents: number;
  feeCents: number;
  remittedBy: 'provider' | 'company';
}

const cad = (cents: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);

/**
 * What a funded period says in place of Record the funding (QA-799): what was taken and when, its
 * fee, and who sends CRA the deductions.
 */
export function fundingWords(f: PayrollFunding, thisYear?: number): string {
  const fee = f.feeCents ? `, with its ${cad(f.feeCents)} fee` : '';
  const cra =
    f.remittedBy === 'provider'
      ? 'The provider sends CRA the deductions and employer costs.'
      : 'The deductions and employer costs are the company’s to send CRA.';
  return `Funded: the provider took ${cad(f.amountCents)} on ${dayText(f.fundedOn, thisYear)}${fee}. ${cra}`;
}
